import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Standalone component that embeds the KADS Angular Elements bundle.
 *
 * Mirrors the pattern of ReactEmbeddedComponent:
 *  1. Dynamically injects <script> + optional <link> into <head>.
 *  2. Creates a <kads-spdm> custom element and appends it to the container.
 *  3. Passes all context as DOM *properties* (not attributes) so JWT is
 *     never exposed in outerHTML.
 *  4. Listens for kads-error / kads-ready DOM events bubbling up.
 *  5. Removes the custom element on ngOnDestroy.
 *
 * @example
 * <miq-kads-embedded
 *   [kadsBaseUrl]="'http://localhost:4300'"
 *   [scriptPath]="'/main.dc5e725beb6531d2.js'"
 *   [runtimePath]="'/runtime.29ec4034361fee02.js'"
 *   [cssPath]="null"
 *   [jwt]="token"
 *   [parentApp]="'MarketIQ'"
 *   [userId]="userId"
 *   (handleError)="onError()">
 * </miq-kads-embedded>
 */
@Component({
  selector: 'miq-kads-embedded',
  standalone: true,
  imports: [CommonModule],
  template: '<div #kadsContainer></div>',
})
export class KadsEmbeddedComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('kadsContainer', { static: false }) containerRef: ElementRef<HTMLDivElement>;

  /** Base URL of the KADS elements server (e.g. https://kads.cbre.com) */
  @Input() kadsBaseUrl: string;

  /** Relative JS bundle path from version.json mainjs */
  @Input() scriptPath: string;

  /** Relative runtime JS bundle path (webpack runtime) */
  @Input() runtimePath: string;

  /** Relative CSS path from version.json maincss (optional) */
  @Input() cssPath: string | null = null;

  /** Identifies the host application to the KADS element */
  @Input() parentApp = 'MarketIQ';

  /** Current user id */
  @Input() userId: string | number;

  /** Optional initial route inside KADS */
  @Input() initialRoute?: string;

  /** Emitted when kads-error fires or script loading fails */
  @Output() handleError = new EventEmitter<void>();

  /** Emitted once the <kads-spdm> element fires kads-ready */
  @Output() kadsReady = new EventEmitter<void>();

  /**
   * Emitted once the <kads-spdm> element fires kads-space-exhausted, i.e. the
   * currently open space's issue list has been emptied by Skip (Story 16).
   */
  @Output() spaceExhausted = new EventEmitter<void>();

  /**
   * Emitted once the <kads-spdm> element fires kads-space-completed, i.e. "Save &
   * Next" successfully submitted the current space's staged edit to EDP (Story 17,
   * Gap 4). Distinct from `spaceExhausted`: this fires regardless of whether the
   * current space still has other outstanding issues.
   */
  @Output() spaceCompleted = new EventEmitter<void>();

  /**
   * Emitted once the <kads-spdm> element fires kads-exit-requested, i.e. "Save &
   * Exit" successfully submitted the current space's staged edit to EDP and the
   * reviewer chose to leave rather than continue to the next space.
   */
  @Output() exitRequested = new EventEmitter<void>();

  $isReady = signal(false);

  private kadsElement: HTMLElement | null = null;
  private loadingPromise: Promise<void> | null = null;
  private routerSub: Subscription | null = null;
  private destroyed = false;

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    if (!this.kadsBaseUrl) {
      this.logger.error('KadsEmbeddedComponent: kadsBaseUrl is required');
      this.handleError.emit();
      return;
    }
    void this.loadAndMount();

    // When MarketIQ's router navigates back to the bare /data-stack route (e.g.
    // via the sidebar menu), history.pushState changes the URL but does NOT fire
    // a popstate event, so the KADS internal router loses sync.  We dispatch a
    // custom event that KadsPrefixedLocationStrategy handles to re-read the
    // browser URL and trigger the appropriate KADS navigation.
    this.routerSub = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      if (e.urlAfterRedirects === '/data-stack' && this.kadsElement) {
        window.dispatchEvent(new CustomEvent('kads-external-navigation'));
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Push userId updates to the live custom element without a full reload
    if (this.kadsElement) {
      if (changes['userId']?.currentValue !== undefined) {
        (this.kadsElement as any)['userId'] = changes['userId'].currentValue;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.routerSub?.unsubscribe();
    if (this.kadsElement) {
      this.kadsElement.removeEventListener('kads-error', this.onKadsError);
      this.kadsElement.removeEventListener('kads-ready', this.onKadsReady);
      this.kadsElement.removeEventListener('kads-space-exhausted', this.onSpaceExhausted);
      this.kadsElement.removeEventListener('kads-space-completed', this.onSpaceCompleted);
      this.kadsElement.removeEventListener('kads-exit-requested', this.onExitRequested);
      this.kadsElement.remove();
      this.kadsElement = null;
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  private async loadAndMount(): Promise<void> {
    if (this.loadingPromise) {
      await this.loadingPromise;
      return;
    }

    this.loadingPromise = this.loadScripts();
    await this.loadingPromise;
    this.loadingPromise = null;
  }

  private async loadScripts(): Promise<void> {
    try {
      if (this.cssPath) {
        await this.loadStylesheet(`${this.kadsBaseUrl}${this.cssPath}`);
      }
      // Both are inserted in order (runtime first) so type="module" guarantees runtime executes
      // before main regardless of which finishes downloading first. Promise.all lets them
      // download in parallel, eliminating the ~5s sequential wait.
      await Promise.all([
        this.loadScript(`${this.kadsBaseUrl}${this.runtimePath}`, true),
        this.loadScript(`${this.kadsBaseUrl}${this.scriptPath}`, true),
      ]);
      this.mountElement();
    } catch {
      this.logger.error('KadsEmbeddedComponent: Failed to load KADS elements bundle');
      this.handleError.emit();
    }
  }

  private loadScript(src: string, isModule = false): Promise<void> {
    return new Promise((resolve, reject) => {
      const sanitized = this.sanitizer.sanitize(SecurityContext.URL, src);
      if (!sanitized) {
        reject(new Error(`KadsEmbeddedComponent: URL failed sanitization: ${src}`));
        return;
      }

      const selector = isModule
        ? `script[src="${sanitized}"][type="module"]`
        : `script[src="${sanitized}"]:not([type="module"])`;
      const existing = document.querySelector(selector);
      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = sanitized;
      script.async = true;

      if (isModule) {
        script.type = 'module';
      }

      // Script loaded — Angular Elements bootstrap happens asynchronously after
      // this point. mountElement() uses customElements.whenDefined() to wait.
      script.onload = () => resolve();

      script.onerror = () => {
        this.logger.error('KadsEmbeddedComponent: Failed to load script');
        reject(new Error(`Failed to load script: ${sanitized}`));
      };

      document.head.appendChild(script);
    });
  }

  private loadStylesheet(href: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const sanitized = this.sanitizer.sanitize(SecurityContext.URL, href);
      if (!sanitized) {
        reject(new Error(`KadsEmbeddedComponent: CSS URL failed sanitization: ${href}`));
        return;
      }
      const existing = document.querySelector(`link[href="${sanitized}"]`);
      if (existing) {
        resolve();
        return;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = sanitized;
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${sanitized}`));
      document.head.appendChild(link);
    });
  }

  // ── Mount ──────────────────────────────────────────────────────────────────

  private mountElement(): void {
    if (!this.containerRef?.nativeElement) {
      return;
    }

    // Angular Elements registers customElements.define('kads-spdm') asynchronously
    // inside ngDoBootstrap, after all APP_INITIALIZERs complete (profile + feature
    // flags HTTP calls). whenDefined() resolves the moment it's registered — no
    // polling needed. The race() enforces a 30s hard cap so the error page still
    // shows if bootstrap hangs (e.g. feature flags API unreachable).
    const BOOTSTRAP_TIMEOUT_MS = 60_000;
    const defined = customElements.whenDefined('kads-spdm');
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('timeout')), BOOTSTRAP_TIMEOUT_MS);
    });

    Promise.race([defined, timeout])
      .then(() => {
        if (this.destroyed) return;
        this.createKadsElement();
      })
      .catch(() => {
        if (this.destroyed) return;
        this.logger.error(
          `KadsEmbeddedComponent: kads-spdm element was not defined within ${BOOTSTRAP_TIMEOUT_MS / 1000}s.` +
            ' Check that the KADS Elements bundle bootstrapped without errors (feature-flags or profile API may be slow).'
        );
        this.handleError.emit();
      })
      .finally(() => clearTimeout(timeoutId));
  }

  private createKadsElement(): void {
    const el = document.createElement('kads-spdm');

    // Use properties (not attributes) so that the element contract is explicit
    (el as any)['parentApp'] = this.parentApp;
    (el as any)['userId'] = this.userId;
    if (this.initialRoute) {
      (el as any)['initialRoute'] = this.initialRoute;
    }
    if (this.kadsBaseUrl) {
      (el as any)['baseUrl'] = this.kadsBaseUrl;
    }

    el.addEventListener('kads-error', this.onKadsError);
    el.addEventListener('kads-ready', this.onKadsReady);
    el.addEventListener('kads-space-exhausted', this.onSpaceExhausted);
    el.addEventListener('kads-space-completed', this.onSpaceCompleted);
    el.addEventListener('kads-exit-requested', this.onExitRequested);

    this.kadsElement = el;
    this.containerRef.nativeElement.appendChild(el);
  }

  // ── DOM event handlers ─────────────────────────────────────────────────────

  private onKadsError = (): void => {
    this.logger.error('KadsEmbeddedComponent: kads-error received');
    this.handleError.emit();
  };

  private onKadsReady = (): void => {
    this.$isReady.set(true);
    this.kadsReady.emit();
    this.logger.info('KadsEmbeddedComponent: kads-ready — KADS element bootstrapped');
  };

  private onSpaceExhausted = (): void => {
    this.spaceExhausted.emit();
  };

  private onSpaceCompleted = (): void => {
    this.spaceCompleted.emit();
  };

  private onExitRequested = (): void => {
    this.exitRequested.emit();
  };
}
